import { TranslationObject } from '@/types';

export const enMessages: TranslationObject = {
  maintenanceBanner: {
    dismiss: 'Dismiss',
  },
  tipsNews: {
    panel: {
      title: 'Tips & News',
      emptyState: 'No new tips right now.',
      dismiss: 'Got it',
      learnMore: 'Learn more',
      ariaLabel: 'Tips & News',
    },
    tag: {
      new: 'New',
      tip: 'Tip',
      info: 'Info',
      bestPractice: 'Best Practice',
    },
  },
  administration: {
    usageAnalytics: {
      header: 'Usage Analytics',
      description: 'Estimator usage within your tenant over time.',
      fromLabel: 'From',
      toLabel: 'To',
      aggregationLabel: 'Aggregation',
      aiAdvisor: 'AI Advisor',
      guidedMode: 'Guided Mode',
      detailedMode: 'Detailed Mode',
      aiReview: 'AI Review',
      humanReview: 'Human Review',
      summaryStartedCompleted: '{started} started · {completed} completed',
      emptyState: 'No usage data for the selected filters',
      exportLabel: 'Export CSV',
      coverage: {
        detailedMode:
          'Includes legacy estimations created before guided/detailed mode was recorded.',
        guidedMode:
          'Only estimations created after mode tracking was introduced are attributed to guided mode.',
        humanReview: 'Only estimations reviewed after review tracking was introduced are counted.',
      },
      series: {
        uses: 'Uses',
        started: 'Started',
        completed: 'Completed',
      },
      agg: {
        day: 'Day',
        week: 'Week',
        month: 'Month',
        quarter: 'Quarter',
        year: 'Year',
      },
    },
    estimationItemParameter: {
      table: {
        header: {
          order: '#',
          label: 'Label',
          parameter: 'Parameter',
          defaultValue: 'Default value',
        },
      },
      addFieldButton: 'Add field',
      wrongFormat: 'Wrong format.',
    },
    header: 'Administration',
    tipsNews: {
      header: 'Tips & News',
      description:
        'Publish Tips & News items for the users of your tenant, in their selected language.',
      createLabel: 'New item',
      emptyState: 'No Tips & News items yet. Create one to get started.',
      colTag: 'Tag',
      colTitle: 'Title',
      colStatus: 'Status',
      colValidity: 'Validity',
      colActions: 'Actions',
      tagNew: 'New',
      tagTip: 'Tip',
      tagInfo: 'Info',
      tagBestPractice: 'Best Practice',
      statusDraft: 'Draft',
      statusActive: 'Active',
      statusScheduled: 'Scheduled',
      statusExpired: 'Expired',
      formCreateTitle: 'New item',
      formEditTitle: 'Edit item',
      fieldTag: 'Tag',
      fieldTitleEn: 'Title (English)',
      fieldTitleDe: 'Title (German)',
      fieldBodyEn: 'Body (English)',
      fieldBodyDe: 'Body (German)',
      fieldCtaLabelEn: 'CTA label (English)',
      fieldCtaLabelDe: 'CTA label (German)',
      fieldCtaUrl: 'CTA URL',
      fieldValidFrom: 'Valid from',
      fieldValidUntil: 'Valid until',
      fieldStatus: 'Status',
      statusDraftOption: 'Draft (hidden from users)',
      statusActiveOption: 'Active (visible to users)',
      languageWarning:
        'One language is empty. You can still activate — users with that language see the other as a fallback.',
      fallbackHint:
        'Each user sees the version for their UI language; an empty language falls back to the other.',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: 'Delete this Tips & News item? This cannot be undone.',
      saveSuccess: 'Tips & News item saved.',
      saveError: 'Failed to save Tips & News item.',
      ctaUrlError: 'CTA URL must be an absolute http(s) URL (e.g. https://example.com).',
      dateOrderError: 'Valid from must be earlier than valid until.',
      activeNeedsContentError: 'An active item needs a title and body in at least one language.',
      deleteSuccess: 'Tips & News item deleted.',
      deleteError: 'Failed to delete Tips & News item.',
      noTitle: '(untitled)',
      none: '—',
    },
    tab: {
      usageAnalytics: {
        label: 'Usage Analytics',
      },
      generalSettings: {
        label: 'General settings',
      },
      tipsNews: {
        label: 'Tips & News',
      },
      ai: {
        label: 'AI',
      },
      metadata: {
        label: 'Metadata',
      },
      assumptions: {
        label: 'Assumptions',
      },
      methodologies: {
        label: 'Methodologies',
        header: 'Manage project types',
        createNew: 'Create new methodology',
        methodologyEditComponentTitle: 'Methodology: {name}',
        nameFieldLabel: 'Name:',
        nameFieldPlaceholder: 'Project type name',
        descriptionFieldLabel: 'Description:',
        descriptionFieldPlaceholder: 'Enter description',
        fieldsLabel: 'Fields:',
        formulaFieldLabel: 'Formula:',
        formulaFieldPlaceholder: 'Enter calculation formula',
        apiLoadDataError: 'Can not load data from server',
        apiSaveDataSuccessful: 'Methologies saved succesfully',
        apiSaveDataError: 'Unexpected error while saving the data',
        requiredFieldLabel: 'This field is required',
        atLeastOneParameterRequired: 'At least one parameter is required',
        formulaHelpText:
          'Use arithmetic operators (+, -, *, /, %), parentheses, param[parameterName]',
        formulaValidationError: 'Invalid formula syntax',
      },
      estimationGroups: {
        label: 'Estimation groups',
      },
      estimationTemplate: {
        label: 'Estimation template',
      },
      resourceTypes: {
        label: 'Resource types',
      },
      costTypes: {
        label: 'Cost types',
      },
      models: {
        apiLoadDataError: 'Can not load data from server',
        apiSaveDataError: 'Unexpected error while saving the data',
        publishConfirmationTitle: 'Confirm publish action',
        publishConfirmationDesc:
          'Do you want to publish estimation model? All changes will be applied and model will be used on new projects.',
        publishConfirmationCompletedTitle: 'Estimation model published',
        publishConfirmationCompletedDesc:
          'Estimation model was published and any new project will use this new configuration.',
        estimationModelNameTitle: 'Estimation model',
        archiveLabel: 'Archive',
        reactivateLabel: 'Reactivate',
        archiveConfirmationTitle: 'Archive estimation model',
        archiveConfirmationModelName: 'Model: {name}',
        archiveConfirmationAffectedEstimations:
          '{count, plural, one {# existing estimation uses this model.} other {# existing estimations use this model.}}',
        archiveConsequenceEstimationsKeepWorking: 'Existing estimations remain fully functional.',
        archiveConsequenceBanner: 'Existing estimations show an "archived model" info banner.',
        archiveConsequenceNoNewEstimations: 'New estimations cannot select this model.',
        archiveConsequenceReversible: 'You can reactivate the model at any time.',
        reactivateConfirmationTitle: 'Reactivate estimation model',
        reactivateConfirmationDesc:
          'This model will become selectable for new estimations again. The archived-model info banner will be removed from all existing estimations.',
        archivedSuccessfully: 'Estimation model archived',
        reactivatedSuccessfully: 'Estimation model reactivated',
      },
      estimationModels: {
        general: {
          fieldEstimationModelNameLabel: 'Project type model name',
          fieldEstimationModelNamePlaceholder: 'Enter model name',
          fieldDescriptionLabel: 'Description (for administrator only)',
          fieldDescriptionPlaceholder: 'Enter description',
          fieldProjectTypeLabel: 'Project type',
          fieldEstimationUnitsLabel: 'Estimation units',
          versionLabel: 'Estimation model version',
          atLeastOneEstimationUnitShouldBeSelectedError:
            'At least one estimation unit should be selected',
        },
      },
      tenantSettings: {
        label: 'Tenant',
        header: 'Tenant settings',
        nameFieldLabel: 'Tenant name',
        nameFieldPlaceholder: 'Enter name of your tenant',
        descriptionFieldLabel: 'Description',
        descriptionFieldPlaceholder: 'Enter description',
        darkModeLogoFieldLabel: 'Dark mode logo',
        logoFieldLabel: 'Light mode logo',
        logoFieldPlaceholder: 'Choose file...',
        iconFieldButton: 'Current logo',
        languagesLabel: 'Languages',
        languagesLabelNotice: 'at least one needs to be selected',
        defaultLanguageLabel: 'Default language',
        defaultHighlightColorLabel: 'Highlight Color',
      },
      aiWizard: {
        label: 'AI Advisory',
        advisoryDefaultModelLabel: 'Default Estimation Model',
        advisoryDefaultModelDescription:
          'Preselected on the Convert to Estimation page. Leave empty to use the seeded AI Advisory model.',
        advisoryDefaultModelPlaceholder: 'Use the seeded AI Advisory model',
        header: 'AI Advisory Settings',
        aiFeaturesToggleLabel: 'Enable AI Advisory',
        aiFeaturesToggleDescription:
          'Toggle to enable or disable AI Advisory including the AI Advisory tab in the sidebar.',
        aiEnabled: 'Enabled',
        aiDisabled: 'Disabled',
        aiInstructionsLabel: 'Tenant Instructions',
        aiInstructionsPlaceholder:
          'Enter specific instructions for the AI assistant to follow when helping users in this tenant...',
        aiInstructionsDescription:
          "Provide context-specific instructions that will help the AI assistant better understand your organization's processes, terminology, and requirements.",
        promptIdLabel: 'Prompt ID',
        promptIdPlaceholder: 'Enter the prompt ID...',
        promptIdDescription:
          'The unique identifier for the AI prompt template. Leave empty to use the system default.',
        promptVersionLabel: 'Prompt Version',
        promptVersionPlaceholder: 'Enter the prompt version...',
        promptVersionDescription:
          'The version of the prompt template. Leave empty to use the latest version.',
        apiKeyLabel: 'API Key',
        apiKeyPlaceholder: 'Enter your API key...',
        apiSaveDataSuccess: 'AI Advisory settings successfully saved',
        apiSaveDataError: 'Failed to save AI Advisory settings. Please try again.',
        apiKeyDescription:
          'Optional: Provide a custom API key for this tenant. Leave empty to use the system default.',
        organizationIdLabel: 'Organization ID',
        organizationIdPlaceholder: 'Enter your OpenAI organization ID...',
        organizationIdDescription:
          'Optional: Provide a custom organization ID for this tenant. Leave empty to use the system default.',
        projectIdLabel: 'Project ID',
        projectIdPlaceholder: 'Enter your OpenAI project ID...',
        projectIdDescription:
          'Optional: Provide a custom project ID for this tenant. Leave empty to use the system default.',
        testConnectionButton: 'Test Connection',
        testConnectionSuccess:
          'Connection test successful! The AI configuration is working correctly.',
        testConnectionError: 'Connection test failed. Please check your configuration.',
        testConnectionTesting: 'Testing connection...',
      },
      aiAdvisoryTabs: {
        settings: 'Settings',
        tenantContext: 'Tenant Context',
      },
      tenantContext: {
        header: 'Tenant Context',
        completeness: '{configured} / {total} sections configured',
        saveTenantContext: 'Save Tenant Context',
        saveSuccess: 'Tenant Context saved successfully.',
        saveError: 'Could not save Tenant Context. Please try again.',
        section1: 'Tenant Identity',
        section2: 'Geography & Operating Footprint',
        section3: 'Regulatory & Legal Environment',
        section4: 'Technology & Architecture Standards',
        section5: 'Organizational Structure & Ways of Working',
        section6: 'Security Posture & Access',
        section7: 'Non-Functional Defaults',
        section8: 'Resource Costs',
        section9: 'Risk Appetite & Change Culture',
        section10: 'Client Mandatory Deliverables',
        s1FieldName: 'Name',
        s1FieldIndustry: 'Industry / sector',
        s1FieldSubIndustry: 'Sub-industry / business line',
        s1FieldCompanySize: 'Company size',
        s2FieldCountries: 'Countries / regions of operation',
        s2FieldPrimaryRegions: 'Primary regions',
        s2FieldLanguages: 'Languages',
        s2FieldTimeZones: 'Time zones',
        s3FieldRegulations: 'Applicable regulations',
        s3FieldDataSensitivity: 'Data sensitivity',
        s3FieldCompliancePosture: 'Compliance posture',
        s4FieldHostingModel: 'Hosting model',
        s4FieldCloudProviders: 'Cloud providers',
        s4FieldTechStack: 'Tech stack',
        s4FieldIntegrationPatterns: 'Integration patterns',
        s4FieldDatabases: 'Databases',
        s4FieldDevOpsTooling: 'DevOps tooling',
        s5FieldDeliveryModel: 'Delivery model',
        s5FieldMethodologies: 'Methodologies',
        s5FieldWaysOfWorking: 'Ways of working',
        s6FieldSecurityLevel: 'Security level',
        s6FieldIdentityStandards: 'Identity standards',
        s6FieldDataClassification: 'Data classification',
        s6FieldSecurityReview: 'Security review expectation',
        s7FieldAvailability: 'Availability target',
        s7FieldPerformance: 'Performance expectations',
        s7FieldSupportHours: 'Support hours',
        s7FieldEnvironments: 'Environments',
        s7FieldDocumentation: 'Documentation expectations',
        s8FieldDayRates: 'Day rates per resource category',
        s9FieldRiskAppetite: 'Risk appetite',
        s9FieldChangeManagement: 'Change management rigor',
        s9FieldOversightStyle: 'Project oversight style',
        s10FieldDeliverables: 'Mandatory deliverables (list)',
      },
      exportSettings: {
        label: 'Export Settings',
        header: 'Export Settings',
        exportSettingsTitle: 'Export Styling',
        exportSettingsDescription:
          'Customize the appearance of PDF and Excel exports. These settings will be applied to both export formats.',
        tableHeadersLabel: 'Table Headers',
        tableRowsLabel: 'Table Rows',
        tableSummaryLabel: 'Table Summary',
        fontColorLabel: 'Font Color',
        backgroundColorLabel: 'Background Color',
        fontFamilyLabel: 'Font Family',
        boldLabel: 'Bold',
        italicLabel: 'Italic',
        fontFamilyRoboto: 'Roboto',
        fontFamilyArial: 'Arial',
        fontFamilyTimes: 'Times New Roman',
        fontFamilyCourier: 'Courier New',
        fontFamilyHelvetica: 'Helvetica',
        apiSaveDataSuccess: 'Export settings successfully saved',
        apiSaveDataError: 'Failed to save export settings. Please try again.',
        apiLoadDataError: 'Failed to load export settings. Please try again.',
      },
      individualisation: {
        label: 'Customization',
        header: 'Individualisation interface strings',
        table: {
          column: {
            label: {
              originalValue: 'Original value',
              location: 'Location',
              customized: 'Customized string',
            },
          },
        },
        successfullySaved: 'Custom labels successfully saved',
        unexpectedErrorDuringSave: 'Unexpected error while saving',
      },
      projectTypes: {
        label: 'Project types',
        header: 'Manage project types',
        createNew: 'Create new project type',
        projectTypeEditComponentTitle: 'Project type: {name}',
        nameFieldLabel: 'Name:',
        nameFieldPlaceholder: 'Project type name',
        descriptionFieldLabel: 'Description:',
        descriptionFieldPlaceholder: 'Enter description',
        iconFieldLabel: 'Icon:',
        iconFieldButton: 'Select icon',
        apiLoadDataError: 'Can not load data from server',
        apiSaveDataSuccessful: 'Project types succesfully saved',
        apiSaveDataError: 'Unexpected error while saving project types',
        requiredFieldLabel: 'This field is required',
        apiSaveDataSuccess: 'Tenant settings successfully saved',
        fileReadError: 'File could not be read',
        failedToUploadTenantLogos:
          'Failed to upload tenant logos.<br>Only the following formats are supported: {formats}, maximum size is {size}MB.',
      },
      estimationMethodologies: {
        label: 'Methodologies',
      },
      estimationModelsDetail: {
        label: 'Models',
        header: 'Your estimation models',
        createNew: 'Create new model',
        nameFieldLabel: 'Model name',
        nameFieldPlaceholder: 'Active version',
        descriptionFieldLabel: 'Working version:',
        descriptionFieldPlaceholder: 'Project type',
        iconFieldLabel: 'Author',
        showArchivedModels: 'Show archived models',
        archivedBadge: 'Archived',
      },
      users: {
        label: 'Users',
        header: 'Manage your users',
        tableTitleName: 'Name',
        tableTitleSurname: 'Surname',
        tableTitleRole: 'Role',
        tableTitleEmail: 'Email',
        tableTitleStatus: 'Status',
        userStatusActive: 'Active',
        userStatusInactive: 'Inactive',
        setupFederatedUserManagement: 'Setup federated user management',
        addNewUserButton: 'Add user',
        addNewUserModalTitle: 'Add user',
        editUserModalTitle: 'Edit user',
        federatedIdentityModalTitle: 'Setup federated identity',
        userFormFirstnameLabel: 'Firstname',
        userFormFirstnamePlaceholder: 'Enter firstname',
        userFormLastnameLabel: 'Lastname',
        userFormLastnamePlaceholder: 'Enter lastname',
        userFormEmailLabel: 'Email',
        userFormEmailPlaceholder: 'Enter email',
        userFormRoleLabel: 'Role',
        userFormLanguage: 'Language',
        userFormStatusLabel: 'Status (active/inactive)',
        copyUserPassword: 'Copy user password',
        userPassword: 'User password',
        saveUserPassword: 'Save this password, it will not be available later',
        deleteUserTitle: 'Delete user',
        deleteUserConfirmationText: 'Are you sure you want to delete user "{userEmail}"?',
        userDeletedConfirmationTitle: 'Confirmation',
        userDeletedConfirmationText: 'User has been deleted.',
        disableUserTitle: 'Disable user',
        disableUserConfirmationText: 'Are you sure you want to disable user "{userEmail}"?',
        userDisabledConfirmationTitle: 'Confirmation',
        userDisabledConfirmationText: 'User has been disabled.',
      },
      workflow: {
        label: 'Workflow',
        header: 'Manage Workflow Steps',
        nameFieldLabel: 'Name:',
        nameFieldPlaceholder: 'Workflow step name',
        summaryFieldLabel: 'Summary:',
        summaryFieldPlaceholder: 'Enter workflow summary',
        descriptionFieldLabel: 'Description:',
        descriptionFieldPlaceholder: 'Enter description',
        urlFieldLabel: 'URL:',
        urlFieldPlaceholder: 'Enter external URL',
        apiLoadDataError: 'Can not load data from server',
        apiSaveDataSuccessful: 'Workflow steps succesfully saved',
        apiSaveDataError: 'Unexpected error while saving workflow steps',
      },
      notificationSettings: {
        label: 'Notification Settings',
      },
      notificationTemplates: {
        variablePhaseResponsibleFirstName: 'Phase responsible first name',
        variablePhaseResponsibleLastName: 'Phase responsible last name',
        variablePhaseResponsibleEmail: 'Phase responsible email',
        variablePhaseStatus: 'Phase status',
        variablePhaseStatusPrevious: 'Previous phase status',
        variablePhaseDeadline: 'Phase deadline',
        label: 'Notification templates',
        header: 'Notification templates',
        eventLabel: 'Workflow event',
        languageLabel: 'Language',
        subjectLabel: 'Email subject',
        bodyLabel: 'Message body',
        variablesLabel: 'Insert variable',
        apiLoadError: 'Failed to load notification template. Please try again.',
        apiSaveSuccess: 'Notification template successfully saved.',
        apiSaveError: 'Failed to save notification template. Please try again.',
        apiResetSuccess: 'Template reset to default successfully.',
        apiResetError: 'Failed to reset notification template. Please try again.',
        resetButton: 'Reset to default template',
        variableProjectName: 'Project name',
        variableVersionName: 'Version name',
        variablePhaseName: 'Phase name',
        variableAssignedBy: 'Assigned by',
        variableDueDate: 'Due date',
        variableEstimationOwnerFirstName: 'Estimation owner first name',
        variableEstimationOwnerLastName: 'Estimation owner last name',
        variableEstimationOwnerEmail: 'Estimation owner email',
        variableProjectResponsibleFirstName: 'Project responsible first name',
        variableProjectResponsibleLastName: 'Project responsible last name',
        variableProjectResponsibleEmail: 'Project responsible email',
        variableReviewOutcome: 'Review outcome',
        variableReviewerName: 'Reviewer name',
        variableReviewCompletedDate: 'Review completed date',
        variableEstimationLink: 'Estimation link',
        previewInAppButton: 'Preview In-App',
        previewEmailButton: 'Preview Email',
        previewTitle: 'Email preview',
        previewInAppTitle: 'In-app preview',
        previewBanner: 'Preview with sample data',
        previewFormatHtml: 'HTML',
        previewFormatText: 'Text only',
        previewLoading: 'Rendering preview…',
        previewError: 'Failed to render preview. Please try again.',
        statusOverridden: 'Using tenant-specific template for this event and language.',
        statusDefault: 'Using system default template for this event and language.',
        sendTestLabel: 'Send test email to',
        sendTestPlaceholder: 'name@example.com',
        sendTestButton: 'Send test email',
        sendTestInvalidEmail: 'Enter a valid email address.',
        sendTestSuccess: 'Test email sent.',
        sendTestError: 'Failed to send test email. Please try again.',
      },
      notifications: {
        label: 'Notifications',
      },
    },
    estimationModel: {
      ai: {
        title: 'AI',
        subTitle: 'Manage AI settings',
        explanationTitle: 'Explanation',
        explanationNumberOfWords:
          'replaced with the maximum number of desired words in the review (provided as parameter by an end user upon requesting the AI review).',
        explanationLanguage: 'replaced with estimation language.',
        explanationEstimationJson: 'replaced with estimation JSON.',
        explanationProjectDescription:
          'automatically included in the JSON structure within {ESTIMATION_JSON}. This text provides the AI with project context for more accurate feedback.',
        projectDescriptionHelperText:
          'This text provides the AI with project context for more accurate feedback.',
        projectDescriptionJsonExample:
          'The {ESTIMATION_JSON} placeholder will be replaced with a JSON object containing estimation data, including the "projectDescription" field. Example structure: {"modelName": "...", "projectName": "...", "projectDescription": "...", "phases": [...], "items": [...]}',
        explanationAllPlaceholdersRequiredNote: 'Note please that all placeholders are required.',
        aiRequestTemplateFieldLabel: 'AI request format',
        aiRequestTemplateFieldPlaceholder: 'Provide formatted text for AI request',
        allPlaceholdersRequiredError: 'All placeholders are required for the AI response format.',
        aiRequestTemplateFieldExampleTitle: 'Example (double click on example text to use it)',
        aiRequestTemplateFieldExampleText:
          'Generate management summary (with summary of the project, observations, tips to improve effort estimation and key takeaway summary sections) in {NUMBER_OF_WORDS} words in {LANGUAGE} language for this estimation JSON: `{ESTIMATION_JSON}`',
        aiRequestTemplateFieldMaxLengthError:
          'AI request format is too long (max {maxLength} characters allowed).',
        aiEnabledLabel: 'Enable AI for this model',
      },
      assumptions: {
        title: 'Assumption types',
        assumptionEditComponentTitle: 'Assumption: {name}',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Enter assumption name',
        descriptionFieldLabel: 'Description (used as tooltip)',
        descriptionFieldPlaceholder: 'Enter description',
        addNewButton: 'Create assumption',
      },
      costTypes: {
        currenciesHeader: 'Manage currencies',
        addCurrency: 'Add currency',
        currencyNameHeader: 'Currency name',
        currencyFieldPlaceholder: 'Select currency',
        costTypesHeader: 'Manage cost types',
        addCostType: 'Add cost type',
        costTypeComponentTitle: 'Cost type: {name}',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Enter cost type name',
        descriptionFieldLabel: 'Description (used as tooltip)',
        descriptionFieldPlaceholder: 'Enter description',
        addNewButton: 'Add cost type',
        cannotDeleteCostTypeUsedByResourceTypeWarning:
          '"{costTypeName}" is referenced in a Resource type and cannot be deleted. Please remove all references to "{costTypeName}" in Resource Types tab first.',
        addCostTypeGroup: 'Add cost group',
        costTypeGroupComponentTitle: 'Cost group: {name}',
        costTypeGroupComponentNameFieldLabel: 'Group Name',
        costTypesSectionTitle: 'Cost Types',
        dropZonePlaceholder: 'Drop cost types here',
        dropZonePlaceholderNote: 'or create new ones using the button above',
        noCostTypesCreated: 'No cost types created',
      },
      estimationGroups: {
        title: 'Estimation Groups',
        groupsComponentTitle: 'Group: {name}',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Enter estimation group name',
        descriptionFieldLabel: 'Description (used as tooltip)',
        descriptionFieldPlaceholder: 'Enter description',
        forbidCustomEstimationItemFieldLabel: 'Forbid adding custom estimation item',
        addNewButton: 'Create estimation group',
      },
      template: {
        title: 'Estimation structure',
        addNewButton: 'Add estimation item',
        noItemsYet: 'This model has no estimation items yet.',
        itemsFailedToLoad:
          'This model has {count} estimation items on the server, but none of them could be loaded into the editor. Reload the page - and do not save, or the items will be lost.',
        removeParentButton: 'Remove parent',
        removeParentConfirmationText:
          'This item will move to the top level. Its sub-items will move with it.',
        removeParentCompletedTitle: 'Parent removed',
        tableTitlePhaseName: 'Phase name',
        editationModalTitle: 'Estimation item',
        estimationGroupFieldLabel: 'Estimation group',
        parentFieldLabel: 'Item parent',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Name',
        referenceFieldLabel: 'Reference Name',
        referenceFieldPlaceholder: 'Reference',
        descriptionFieldLabel: 'Description',
        descriptionFieldPlaceholder: 'Enter description of item',
        requiredFieldLabel: 'This field is required',
        estimationTypeFieldLabel: 'Estimation type',
        estimationTypeUserModifiable: 'User modifiable',
        estimationTypeParameterized: 'Calculated from parameters',
        estimationTypeCalculated: 'Calculated',
        fieldsFieldLabel: 'Fields',
        formulaFieldLabel: 'Formula',
        formulaFieldPlaceholder: 'Place calculation formula',
        cancelButtonText: 'Cancel',
        saveButtonText: 'Save',
        columnHeaderOrder: '#',
        columnHeaderName: 'Name',
        resourcesTitle: 'Resources',
        costsTitle: 'Non-Personnel Cost',
        whenThereAreNoParamsAndFormulaIsProvidedItemShouldBeCalculatedError:
          'When there are no parameters and formula is provided, the item should be calculated',
        whenFormulaIsProvidedAtLeastOneParameterMustExistError:
          'When formula is provided, at least 1 parameter must exist.',
        formulaMustBeEmptyWhenUserModifiableError:
          'A formula cannot be set when the estimation type is user modifiable.',
        formulaRequiredForCalculatedTypeError:
          'A formula is required for calculated estimation types.',
        wrongFormat: 'Wrong format.',
        formulaHelpText:
          'Use arithmetic operators (+, -, *, /, %), parentheses, param[parameterName], item[itemReference] and functions like sumItemsExcept(...) to calculate the value. For example: param[A] + param[B] + item[itemReference1] + sumItemsExcept(itemReference2, itemReference3)',
      },
      metadata: {
        title: 'Metadata types',
        metadataComponentTitle: 'Metadata: {name}',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Enter metadata name',
        descriptionFieldLabel: 'Description (used as tooltip)',
        descriptionFieldPlaceholder: 'Enter description',
        guidedModeQuestionFieldLabel: 'Guided mode question',
        guidedModeQuestionFieldPlaceholder: 'Enter question for guided mode',
        typeFieldLabel: 'Metadata type',
        typeFieldPlaceholder: 'Select metadata type',
        selectOptionsFieldLabel: 'Selectable options',
        selectOptionsFieldPlaceholder: 'Type each option to separate line',
        mandatoryFieldLabel: 'Mandatory',
        emptyLinesNotAllowed: 'Empty lines are not allowed.',
        pipeNotAllowed: 'Pipe character is not allowed.',
        addNewButton: 'Create metadata',
      },
      methodologies: {
        title: 'Select methodologies',
        manageAllMethodologies: 'Manage all methodologies',
      },
      resourceTypes: {
        resourceTypeEditComponentTitle: 'Resource type: {name}',
        nameFieldLabel: 'Name',
        nameFieldPlaceholder: 'Enter resource type name',
        descriptionFieldLabel: 'Description (used as tooltip)',
        descriptionFieldPlaceholder: 'Enter description',
        addNewButton: 'Add resource type',
        costTypeFieldLabel: 'Cost type',
        costTypeFieldPlaceholder: 'Select cost type of resource type',
        title: 'Resource Types',
        resourceCostFieldLabel: 'Resource cost',
        resourceCostUserModifiable: 'User modifiable',
        addResourceGroup: 'Add resource group',
        resourceGroupComponentTitle: 'Resource group: {name}',
        resourceGroupComponentNameFieldLabel: 'Group Name',
        resourceTypesSectionTitle: 'Resource Types',
        addResourceType: 'Add resource type',
        dropZonePlaceholder: 'Drop resource types here',
        dropZonePlaceholderNote: 'or create new ones using the button above',
        noResourceTypesCreated: 'No Resource Types Created',
      },
    },
  },
  validationErrorMessages: {
    title: 'Estimation structure',
    formulaValidation: {
      consecutiveBrackets:
        'Invalid syntax: consecutive brackets "][" are not allowed. Use operators between references (e.g., param[A] + param[B])',
      unmatchedBrackets: 'Invalid syntax: unmatched or nested brackets. Use {allowedRefs}',
      invalidCharacters: 'Invalid characters found: {invalidChars}',
      invalidParameterName:
        'Invalid parameter name: {paramName}. Only alphanumeric characters, underscores, and hyphens are allowed.',
      parameterNotDefined:
        "Parameter '{paramName}' is not defined. Available parameters: {availableParams}",
      parametersNotAllowed: 'Parameter references (param[...]) are not allowed in this context',
      invalidItemReference:
        'Invalid item reference: {itemName}. Only alphanumeric characters, underscores, and hyphens are allowed.',
      itemsNotAllowed: 'Item references (item[...]) are not allowed in this context',
      invalidFunctionArgument:
        'Invalid function argument: {arg}. Only alphanumeric characters, underscores, and hyphens are allowed.',
      functionsNotAllowed:
        'Function references (sumItemsExcept(...)) are not allowed in this context',
      unbalancedParentheses: 'Unbalanced parentheses in formula',
      emptyParentheses: 'Empty parentheses are not allowed',
      divisionByZero: 'Formula contains division by zero',
      consecutiveOperators: 'Consecutive operators are not allowed',
      operatorAtStart: 'Formula cannot start with +, *, /, or % operators',
      operatorAtEnd: 'Formula cannot end with an operator',
      validationFailed: 'Formula validation failed: {errors}',
      incompleteParameter: 'Parameter name is missing. Use param[parameterName]',
      incompleteItem: 'Item reference is missing. Use item[itemReference]',
    },
  },
  aiReviewParamsModal: {
    title: 'AI Review Parameters',
    helpTitle: 'Please provide the required AI review parameters.',
    helpText:
      'The created request will be honored as quickly as possible, depending on the current load (typically within few minutes). The response will be sent to the email address associated with your account as well you will be notified in the application.',
    maxWordsCount: 'Maximum desired words count',
    language: 'Language',
    createRequest: 'Create Request',
  },
  aiReviewViewModal: {
    title: 'Project AI Review',
    copy: 'Copy',
    forward: 'Forward by email',
    export: 'Export to PDF',
    language: 'Language',
    noReview: 'The estimation contains no AI review for the selected language.',
    reviewCreationDate: 'You are viewing the AI review created at {date}',
  },
  assumptionCreationModal: {
    title: 'Creation of Assumption',
    assumptionType: 'Assumption Type',
    assumptionCategoryDropdownPlaceholder: 'Select Type',
    description: 'Description',
    descriptionPlaceholder: 'Meaningful short description for management (max 210 characters)',
  },
  assumptionDeletionConfirmationModal: {
    title: 'Deleting Assumption',
    warningMessage: 'This will delete the assumption. The operation cannot be reverted.',
    confirmButton: 'Confirm Removing',
  },
  assumptionEditationModal: {
    title: 'Editation of Assumption',
    assumptionType: 'Assumption Type',
    assumptionCategoryDropdownPlaceholder: 'Select Type',
    description: 'Description',
    descriptionPlaceholder: 'Meaningful short description for management (max 210 characters)',
  },
  assumptions: {
    addButton: 'Add new assumption',
    listModal: {
      titleWithPhaseNameFormatted: 'Assumptions for {phaseName}',
    },
    table: {
      header: {
        order: '#',
        assumptionId: 'Assumption ID',
        estimationItem: 'Estimation Item',
        assumptionType: 'Assumption Type',
        assumption: 'Assumption',
        isValid: 'Active',
      },
      export: {
        title: 'Assumptions',
      },
    },
  },
  dropdown: {
    emptyPlaceholder: 'Select',
  },
  durationFormField: {
    wrongFormat: 'Wrong format (DDD.HH:MM).',
  },
  fieldError: {
    required: 'Required',
    wrongValue: 'Wrong value',
    maxLength: 'Text exceeded maximum limit of characters',
  },
  genericError: {
    errorOccured: 'An error occurred, please contact support.',
  },
  localizableFormField: {
    provideTranslation: '[Provide translation]',
    translationFieldPlaceholder: 'Translation for {language}',
  },
  notFound: {
    resourceDoesNotExist:
      'The resource you are trying to reach does not exist, has been moved or could not be found.',
    homePageButton: 'Home page',
  },
  unauthorized: {
    title: '403 - Unauthorized',
    youDoNotHaveAccess: 'You do not have permission to access this page.',
    homePageButton: 'Home page',
  },
  customDirectCostCreationModal: {
    title: 'Create Custom Cost',
    costName: 'Cost Name',
    estimationGroup: 'Estimation Group',
    description: 'Description',
    descriptionPlaceholder: 'Meaningful short description for management (max 210 characters)',
  },
  customEstimationItemCreationModal: {
    title: 'Create Custom Phase',
    phaseName: 'Phase Name',
    estimationGroup: 'Estimation Group',
    noEstimationGroupsAvailable: 'No estimation group is available for this selection.',
    parent: 'Parent',
    parentNone: '(none — create as top-level)',
    phaseLead: 'Phase Lead',
    methodology: 'Methodology',
    description: 'Description',
    descriptionPlaceholder: 'Meaningful short description for management (max 210 characters)',
  },
  comments: {
    modalTitleWithPhaseNameFormatted: 'Comments of {phaseName}',
    contextTypeEstimationLabel: 'Estimation',
    contextTypeResourcingLabel: 'Resource',
    contextTypeCostingLabel: 'Costs',
    typeMessagePlaceholder: 'Type your message',
    hintTitle: 'Press Ctrl (Cmd on Mac) + Enter to send and Esc to close window',
    messageLabel: 'Message:',
    postingComment: 'Posting comment',
    emptyState: 'No comments yet — start the conversation.',
  },
  dashboard: {
    estimationButtons: {
      continue: 'Continue estimation of',
      noEstimationsYet: "You don't own any estimations yet",
      createNew: 'Create a new estimate',
      newEstimation: 'New estimation project',
      strategy: 'Strategy',
    },
    steps: {
      title: 'How to use the Estimator?',
      buttons: {
        back: 'Back to {previousStep}',
        continue: 'Continue to {nextStep}',
        expand: 'Expand',
        collapse: 'Collapse',
        learnAbout: 'Learn about {currentStep}',
        showLess: 'Show less',
      },
      guide: {
        aiAdvisory: {
          url: '',
          title: 'AI Advisory',
          description: {
            short:
              'Optional pre-step: start a conversation with the AI Advisor to generate an initial draft estimate from a short project description.',
            long: 'Before creating a new estimation manually, you can use the AI Advisory as an optional pre-step. The AI Advisor helps you produce an initial estimate from a short project description, which you can then continue to refine in the following steps. This step is entirely optional — you can also start directly with Setup.',
          },
        },
        basics: {
          url: '',
          title: 'Setup',
          description: {
            short:
              'Please enter the basics of the project here, such as the project name, type, involved persons, and the estimation method in Esti Mate.',
            long: 'In the first step, you need to enter the basic data of the project. This includes the project name, type, involved persons, and the selected estimation methodology. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        chooseMode: {
          url: '',
          title: 'Choose Mode',
          description: {
            short:
              'After Setup, choose how to continue: Estimation Wizard Mode (guided, step-by-step) or Estimation Detailed Mode (direct full control).',
            long: 'After completing Setup, you decide how to continue your estimation. Choose Estimation Wizard Mode for a guided, step-by-step experience that walks you through each phase — recommended for standard flows or less experienced users. Choose Estimation Detailed Mode to jump straight into the full detailed view, for advanced users or fine-tuning. The detailed process steps that follow (Master Data, Phases, Estimate, Resource, Costs, Review) are the same and accessible via either mode.',
          },
        },
        metadata: {
          url: '',
          title: 'Master Data',
          description: {
            short:
              'Please capture the central framework conditions of your project, including objectives, scope, and rough timeline.',
            long: 'In the second step, you need to enter the central framework conditions of your project. This includes the project name, type, involved persons, and the selected estimation methodology. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        projectPhases: {
          url: '',
          title: 'Phases',
          description: {
            short:
              'Please divide the project into different phases and document specific planning assumptions for each phase.',
            long: 'In the third step, you need to divide the project into different phases and document specific planning assumptions for each phase. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        estimating: {
          url: '',
          title: 'Estimate',
          description: {
            short:
              'Please estimate the effort for each project phase/subphase (work package) and calculate the necessary resource usage.',
            long: 'In the third step, you need to estimate the effort for each project phase/subphase (work package) and calculate the necessary resource usage. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        resourcing: {
          url: '',
          title: 'Resource',
          description: {
            short:
              'Please assign the personnel resources, divided into internal/external and specialist/IT resources, to the tasks.',
            long: 'In the third step, you need to assign the personnel resources, divided into internal/external and specialist/IT resources, to the tasks. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        costing: {
          url: '',
          title: 'Costs',
          description: {
            short:
              'Please calculate the project costs using the daily rates of the resource groups and add any applicable operating expenses.',
            long: 'In the third step, you need to calculate the project costs using the daily rates of the resource groups and add any applicable operating expenses. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
        review: {
          url: '',
          title: 'Review',
          description: {
            short:
              'Here you have the option to verify your estimate through an AI-assisted validation and/or have it reviewed by another project manager through a peer review.',
            long: 'In the third step, you need to verify your estimate through an AI-assisted validation and/or have it reviewed by another project manager through a peer review. This information forms the basis for the further process. Ensure that these details are precise and complete.',
          },
        },
      },
    },
  },
  export: {
    pdf: {
      pageNOfM: 'Page {currentPageNumber} of {totalPagesCount}',
      poweredBy: 'Powered by Estimation.World',
    },
    estimation: {
      masterdata: 'Masterdata',
      reference: '#',
      group: 'Group',
      phase: 'Phase',
      phaseLead: 'Phase Lead',
      methodology: 'Methodology',
      estimate: 'Estimate',
      estimateCheck: 'Estimate Check',
      costsSum: 'Costs Sum',
      total: 'Total',
      assumptions: 'Assumptions',
    },
  },
  deleteProjectButton: {
    deleteEstimationConfirmationText: 'Are you sure you want to delete estimation "{projectName}"?',
    estimationDeletedConfirmationTitle: 'Confirmation',
    estimationSuccessfullyDeleted: 'Estimation has been successfully deleted.',
  },
  estimates: {
    error: {
      loadingFailed: 'Could not load estimations.',
      retry: 'Retry',
    },
    filters: {
      showOnlyMyLabel: 'Show only my estimates',
      projectTypeLabel: 'Project Type',
      statusLabel: 'Status',
    },
    title: 'Estimates',
    table: {
      header: {
        projectName: 'Project Name',
        planningResponsible: 'Planning Responsible',
        method: 'Method',
        projectType: 'Project Type',
        status: 'Status',
        estimate: 'Estimate',
        estimationUnit: 'Estimation Unit',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
        createdBy: 'Created By',
      },
    },
    newEstimation: 'New Estimation',
    myEstimations: 'My Estimations',
    allEstimations: 'All Estimations',
    continue: 'Continue',
  },
  header: {
    notifications: {
      title: 'Notifications',
      readAll: 'Mark all as read',
      showAll: 'Show all notifications',
      noNew: 'No new notifications.',
      all: 'All',
      unread: 'Unread',
      user: {
        assignedAsContributorToEstimation:
          'You have been assigned as a contributor to estimation ({projectName}).',
        assignedAsPhaseLead:
          'You have been assigned as a phase lead to estimation item ({projectName}).',
        assignedAsPlanningResponsibleToEstimation:
          'You have been assigned as a planning responsible to estimation ({projectName}).',
        assignedAsReviewerToEstimation:
          'You have been assigned as a reviewer to estimation ({projectName}).',
      },
      review: {
        requested: 'Review requested on estimation item ({projectName}).',
        approved: 'Estimation item approved ({projectName}).',
        cancelled: 'Estimation review cancelled ({projectName}).',
        outcomeCompleted: 'The estimation review you own has been completed ({projectName}).',
        outcomeCanceled: 'The estimation review you own has been canceled ({projectName}).',
      },
      comment: {
        addedToEstimationItem: 'A comment has been added to estimation item ({projectName}).',
      },
      estimation: {
        newVersion: 'New version of estimation was created ({projectName}).',
        aiReview: 'AI review has been created for estimation ({projectName}).',
      },
      reviewCommentsSummary: {
        available: 'Review comments summary is available for estimation ({projectName}).',
      },
      phase: {
        statusChanged: 'The status of a phase has changed ({projectName}).',
        blocked: 'A phase has been blocked ({projectName}).',
        unblocked: 'A phase has been unblocked ({projectName}).',
        itemAssigned: 'An item has been assigned to a phase ({projectName}).',
        deadlineApproaching: 'A phase deadline is approaching ({projectName}).',
      },
      conversation: {
        shared: 'An AI Advisory conversation has been shared with you.',
      },
      deletedProject: 'deleted',
      newCount: '{count} New',
      markAsRead: 'Mark as read',
    },
  },
  newEstimation: {
    costs: {
      title: 'Assign Costs to your Project Phases?',
      skip: 'Skip',
      startAddingCosts: 'Start Adding Costs',
      costHeader: 'Cost',
      selectCurrency: 'Select Cost Currency',
      costCurrency: 'Cost Currency',
      selectCurrencyPlaceholder: 'Select',
      notNow: 'Cancel',
      save: 'Save',
      empty: '![Empty]',
      assignCostsForResourceType: 'Set Cost for each Resource Type',
      total: 'Total',
      subTotal: 'Subtotal',
      notIncluded: 'Not Included',
      costSummary: 'Cost Summary',
      phaseUnit: 'Estimation Unit: {unit}',
      costUnit: 'Cost Unit: {currencyCode}',
      highLevelDirectCostEstimation: 'Provide high-level non-personnel cost estimation',
      resourceCostsTableTitle: 'Resource Costs',
      directCostsTableTitle: 'Non-Personnel Costs',
      directCosts: 'Direct Costs',
      totalProjectCost: 'Total Project Cost',
      grandTotal: 'Grand Total',
    },
    estimating: {
      phaseHeaderSuffix: 'Estimation',
      renamePhase: 'Rename phase',
      savePhaseName: 'Save phase name',
      cancelPhaseRename: 'Cancel phase rename',
      renamePhaseError: 'Phase could not be renamed',
      assumptionQuestion: 'Would you like to note any Assumptions for {name} phase?',
      noAssumptionsProvided: 'No {name} phase assumptions provided.',
      selectMethodology: 'Select methodology for {name}',
      title: 'Estimate your Project Phases?',
      startEstimating: 'Start Estimation',
      skipEstimating: 'Skip',
      phaseHeader: '{name} Estimation',
      phaseTitle: 'Estimation methodology for this phase',
      phaseWithInput: 'Please provide your high level estimate in {estimationUnit}',
      phaseWithInputPlaceholder: 'No. of {estimationUnit}',
      phaseName: 'Phase Name',
      phaseValueCancel: 'Cancel',
      phaseValueSave: 'Save',
      methodology: 'Methodology',
      estimate: 'Estimate',
      notIncluded: 'Not Included',
      estimatingSummary: 'Estimating Summary',
      total: 'Total',
      phaseUnit: 'Estimation Unit: {unit}',
      suggestedValueFormat: '{label} (Suggested value: {value} {label})',
      suggestedValueFormatWithUserValue: '{userValue} {label} (Suggested value: {value} {label})',
      fixedValueFormat: '{label} (Fixed value: {value} {label})',
      calculatedValueFormat: 'Calculated value: {value} {unit}',
      summary: 'Summary',
      assumptionsButton: 'Assumptions',
    },
    metadata: {
      mandatory: {
        title: 'Mandatory Metadata',
        subtitle: '{providedPercentage}% of mandatory information has been provided',
      },
      optional: {
        title: 'Optional Metadata',
        subtitle: 'Do you want to provide more details on your project?',
      },
      emptyValueLabel: 'Empty',
      noMandatoryMetadataDefinedSubtitle: 'No mandatory metadata is defined',
      noOptionalMetadataDefinedSubtitle: 'No optional metadata is defined',
      invalidUrl: 'Enter a valid URL',
    },
    phaseAssumption: {
      decline: 'Cancel',
      save: 'Save',
      selectAssumptionType: 'Select assumption type',
      provideYourAssumption: 'Your assumption',
      assumptionType: 'Type',
      assumptionDescription: 'Description',
    },
    phaseWithSelect: {
      placeholder: 'Select',
      empty: 'Empty',
    },
    projectBasics: {
      methodology: {
        title: 'Methodology',
        subtitle: 'Choose your default estimation approach',
        defaultEstimationUnitLabel: 'Default estimation unit',
      },
      projectInfo: {
        title: 'Start Your Estimator Project',
        subtitle:
          'Please answer a few questions and Estimator will prepare a basic estimation overview for you.',
        projectNameLabel: 'Project Name',
        planningResponsibleLabel: 'Planning Responsible',
        reviewerLabel: 'Reviewer',
        additionalContributorsLabel: 'Project Contributors',
        estimationLanguageLabel: 'Estimation Language ',
        projectTypeLabel: 'Project Type',
        methodologyLabel: 'Estimation Methodology',
        defaultEstimationUnitLabel: 'Default estimation unit',
        projectDescriptionLabel: 'Project Description',
        projectDescriptionHelpText:
          'Please describe the purpose, scope, and context of the project.',
      },
      projectType: {
        title: 'Project Type',
        subtitle:
          'Choose the type of project you are working on to get started with the estimation process.',
        estimationModelNotDefined: 'Estimation model is not defined. Please contact Administrator.',
      },
    },
    projectPhases: {
      renamePhase: 'Rename phase',
      savePhaseName: 'Save phase name',
      cancelPhaseRename: 'Cancel phase rename',
      renamePhaseError: 'Phase could not be renamed',
      title: 'Project phases',
      loading: 'Loading...',
      phaseName: 'Phase name',
      included: 'Included',
      addPhase: 'Add Project Phase',
      editAssumptions: 'Assumptions ({count})',
      creatingPhasesInProcess: 'Creating phases, this might take a moment...',
    },
    addAssumptionModal: {
      title: 'Add Assumption',
    },
    review: {
      title: 'Review Your Estimation',
      subtitle: '{providedPercentage}% of mandatory information has been provided',
      emptyValue: '[Empty]',
      noMandatoryMetadataDefinedSubtitle: 'No mandatory metadata has been defined',
      aiReviewEnqueueLabel: 'Initiate AI Review',
      aiReviewSuccessfullyEnqueued:
        'AI review has been successfully initiated, you will get a notification once it is completed.',
    },
    resource: {
      title: 'Assign Resources to your Project Phases?',
      skip: 'Skip',
      startResourcing: 'Start Adding Resources',
      resourceHeader: 'Resource {name}',
      phaseUnit: 'Estimation Unit: {unit}',
      methodology: 'Methodology',
      estimate: 'Estimate',
      delta: 'Delta',
      assignResourcesForPhase: 'Assign resources for phase in {unit}',
      valueSave: 'Save',
      valueNotNow: 'Cancel',
      summary: 'Summary',
      resourceSummary: 'Resource Summary',
      subTotal: 'Subtotal',
      total: 'Total',
      notIncluded: 'Not included',
    },
    header: {
      salutation: 'Welcome to Estimator Estimation Project Wizard, {username}!',
      unsaved: 'Unsaved version for project {projectName}',
      saved: '{versionName} for project {projectName}',
    },
    title: 'Estimation steps',
    buttons: {
      cancel: 'Cancel estimation',
      nextStep: 'Next step',
      previousStep: 'Previous step',
      createProject: 'Create Project',
      finishWizard: 'Finish Wizard',
    },
    continueEstimation: {
      loading: 'Please wait while we are retrieving the data of your last estimation...',
    },
    setup: {
      title: 'Create New Estimate',
      subtitle:
        'Set up the basic details for your estimation project. All fields marked with * are required.',
      projectInfoSection: 'Project Information',
      projectTypeSection: 'Project Type',
      methodologySection: 'Estimation Methodology',
      projectNameLabel: 'Project Name',
      planningResponsibleLabel: 'Planning Responsible',
      reviewerLabel: 'Reviewer',
      additionalContributorsLabel: 'Project Contributors',
      estimationLanguageLabel: 'Estimation Language',
      projectTypeSubtitle: 'Choose the type of project you are working on.',
      estimationModelNotDefined:
        'Estimation model is not defined for this project type. Please contact Administrator.',
      methodologySubtitle: 'Choose your default estimation approach.',
      defaultEstimationUnitLabel: 'Default Estimation Unit',
      continueButton: 'Create & Continue',
      cancelButton: 'Cancel',
      createError: 'Failed to create estimation. Please try again.',
    },
    chooseMode: {
      title: 'Choose your workflow',
      subtitle: 'Your estimation has been created. Select how you want to continue.',
      wizard: {
        title: 'Estimation Wizard Mode',
        description:
          'Guided, step-by-step setup recommended for standard flows or less experienced users.',
        cta: 'Continue in Wizard Mode',
      },
      detailed: {
        title: 'Estimation Detailed Mode',
        description: 'Jump straight into the full detailed view for advanced users or fine-tuning.',
        cta: 'Continue in Detailed Mode',
      },
    },
  },
  sidebar: {
    dashboard: 'Dashboard',
    estimation: 'Estimates',
    aiAdvisory: 'Estimation AI Advisory',
    newEstimation: 'Create new estimate',
    wizardMode: 'Estimation Wizard',
    newConversation: 'New Conversation',
    detailedEstimation: 'Detailed Estimation',
    notification: 'Notification',
    administration: 'Administration',
    signOut: 'Sign Out',
  },
  convertAdvisory: {
    pageTitle: 'Convert AI Advisory to Estimation',
    pageSubtitle:
      'Pick the phases and assumptions to import, then confirm the project master data. A new Estimation draft will be created.',
    loadAdvisoryError:
      'The work breakdown could not be read from this AI Advisory response. The conversation itself is unaffected.',
    extractionUnavailableError:
      'The AI did not finish reading the work breakdown from this advisory. This usually succeeds on a retry.',
    partialImportNotice:
      '{imported} of {selected} selected work packages were imported. The advisory structure changed in the meantime, so the rest could no longer be matched.',
    emptyStructureHint:
      'You can still create the draft below from the project master data and add phases in the estimation. An answered advisory cannot be continued, so to get a breakdown from the assistant, start a new advisory with more detail.',
    noStructuredData: 'This advisory response contains no work breakdown that can be imported.',
    askAgainButton: 'Ask again in a new advisory',
    selectionHeading: 'What to import',
    wbsSectionTitle: 'Work breakdown ({count})',
    assumptionsSectionTitle: 'Assumptions ({count})',
    selectAll: 'Select all',
    selectNone: 'Select none',
    masterDataHeading: 'Project master data',
    projectTypeLabel: 'Project type',
    projectTypeUnavailableSuffix: 'not available: {reason}',
    requirementNoPublishedModel: 'no published estimation model',
    requirementNoItemGroup: 'its model has no item group',
    requirementNoPersonDaysUnit: 'its model does not offer person-days',
    requirementNoThreePointMethodology: 'its model links no 3-point methodology',
    warningNoCurrency:
      'This model has no currency configured, so the cost tabs will not save on the new estimation.',
    projectTypeHint: 'Decides which estimation model the draft is built on.',
    noProjectTypeAvailable:
      'No project type in this tenant can take an imported estimate yet. Its estimation model needs person-days as a unit and a 3-point methodology.',
    projectNameLabel: 'Project name',
    projectDescriptionLabel: 'Project description',
    documentLinkLabel: 'AI Advisory document link (optional)',
    documentLinkPlaceholder: 'https://…',
    submitButton: 'Create Estimation draft',
    cancelButton: 'Cancel',
    workstreamLabel: 'Workstream:',
    rangeLabel: '{optimistic}–{likely}–{pessimistic} PD',
    submitErrorTitle: 'Convert failed',
    noSelectionWarning:
      'No work packages selected - the draft will be created from the project master data only.',
    masterCheckboxLabel: 'All work packages',
    workstreamCheckboxLabel: 'Toggle all items in {name}',
    selectionCounter: '{wbs} WBS · {assumptions} assumptions · {links} phase links selected',
    orphanedAssumptionsWarning: 'Orphaned assumptions: {count}',
    orphanedAssumptionsHint:
      'These assumptions have no selected target phase and will be anchored to the first selected phase on import.',
    assumptionImpactsLabel: 'Impacts:',
    assumptionNoImpacts: 'No linked phases',
    // EWM-986: conversion progress overlay.
    conversionTitle: 'Converting AI Advisory to Estimation',
    conversionSubtitle: 'Building your detailed estimation tree - this usually takes 1–3 minutes.',
    elapsedLabel: 'Elapsed',
    milestoneFetching: 'Fetching AI Advisory content',
    milestoneParsing: 'Parsing structured sections',
    milestoneBuilding: 'Building estimation tree',
    milestonePreparing: 'Preparing selection preview',
    conversionTip: "You'll be able to select which branches to include in your final estimate.",
    conversionOverrun: 'Taking longer than usual - please continue to wait.',
    cancelConversionButton: 'Cancel conversion',
    retryButton: 'Try again',
    backToAdvisoryButton: 'Back to AI Advisory',
  },
  aiAdvisory: {
    tenantInstructions: '',
    convertToEstimation: 'Convert to Estimation',
    convertNoWorkBreakdown: 'This answer contains no work breakdown, so it cannot be converted.',
    sharedBadge: 'Shared',
    sharedBadgeTitle: 'Shared with you by another user',
    title: 'Estimation AI Advisory',
    subtitle: 'Get intelligent advice for your project estimations',
    welcomeMessage:
      "Hello! I'm your AI Estimation Advisor. I can help you with project estimation, methodology selection, resource planning, and cost analysis. How can I assist you today?",
    placeholder: 'Ask me anything about project estimation...',
    advisoryProvided: 'AI Advisory provided',
    sendButton: 'Send',
    helpText: 'Press Enter to send, Shift+Enter for new line',
    aiResponse:
      'Thank you for your question! This is a placeholder response. In the actual implementation, this would be connected to an LLM service to provide intelligent estimation advice.',
    loadingChatHistory: 'Loading chat history...',
    failedToLoadChatHistory: 'Failed to load chat history',
    prefillQuestionUnavailable:
      'The previous question could not be loaded. Type your question below to start a new advisory.',
    pdfExportFailed: 'The PDF could not be created. Please try again or export as Word.',
    generatingResponse: 'Generating response...',
    activityThinking: 'Thinking...',
    activityWebSearch: 'Searching the web...',
    activityTool: 'Working with {tool}...',
    answerIncomplete: 'Generation was interrupted - this answer may be incomplete.',
    deleteConversation: 'Delete Conversation',
    deleteConversationTitle: 'Delete Conversation',
    deleteConversationWarning:
      'This will permanently delete this conversation and all its messages. This action cannot be undone.',
    deleteConversationConfirm: 'Delete Conversation',
    shareConversation: 'Share Conversation',
    shareConversationTitle: 'Share AI Advisory',
    shareConversationDescription: 'Send a link to this conversation to someone in your tenant.',
    shareConversationEmailLabel: 'Recipient',
    shareConversationRecipientPlaceholder: 'Select user',
    shareConversationSend: 'Send',
    shareConversationCopyLink: 'Copy link',
    shareConversationCancel: 'Cancel',
    shareConversationSuccess: 'Invitation sent',
    shareConversationError: 'Failed to send invitation',
    shareConversationNotAllowed: 'Recipient must be in the same tenant',
    shareConversationNotFound: 'Conversation not found',
    shareConversationEmailRequired: 'Please select a recipient',
    linkCopied: 'Link copied to clipboard',
    newFolder: 'New folder',
    newFolderDefaultName: 'New Folder',
    renameFolder: 'Rename folder',
    renameFolderConfirm: 'Save',
    renameFolderCancel: 'Cancel',
    renameFolderError: 'Failed to rename folder',
    createFolderError: 'Failed to create folder',
    deleteFolder: 'Delete folder',
    deleteFolderTitle: 'Delete Folder',
    deleteFolderWarningWithContents:
      'Delete folder "{name}"? The {count, plural, one {# conversation} other {# conversations}} inside will be moved to the unfoldered list. No conversation will be deleted.',
    deleteFolderWarningEmpty: 'Delete folder "{name}"?',
    deleteFolderConfirm: 'Delete Folder',
    moveToFolder: 'Move to folder',
    moveToFolderError: 'Failed to move conversation',
    moveToFolderNoFolderOption: 'No folder',
    moveToFolderCreateNew: '+ Create new folder',
    renameConversation: 'Rename conversation',
    renameConversationConfirm: 'Save',
    renameConversationCancel: 'Cancel',
    renameConversationError: 'Failed to rename conversation',
  },
  api: {
    enum: {
      ApiEstimationStage: {
        IN_CREATION: 'In Creation',
        IN_PROCESS: 'In Process',
        IN_REVIEW: 'In Review',
        APPROVED: 'Approved',
      },
      ApiEstimationAdditionalFieldType: {
        TEXT: 'Text',
        FORMATTED_TEXT: 'Long text',
        DURATION_TIME_SPAN: 'Duration',
        SELECTABLE: 'Selectable',
        DATE: 'Date',
        DOCUMENT_LINK: 'Document Link / URL',
      },
      EstimationUnit: {
        PD: 'Person days',
        HOURS: 'Hours',
        STORY_POINTS: 'Story points',
        CURRENCY: 'Currency',
      },
    },
  },
  newAppVersionToast: {
    newVersionDeployed:
      'A new version of the application has been deployed. It is recommended to reload the page.',
    reloadButton: 'Click here to reload.',
  },
  estimationNavigation: {
    wizardMode: 'Wizard Mode',
    cancelEstimation: 'Cancel Estimation',
    version: 'version',
    basics: {
      mainTitle: 'Setup',
      project: 'Project',
      projectType: 'Project Type',
      methodology: 'Methodology',
    },
    metadata: {
      mainTitle: 'Master Data',
      mandatory: 'Mandatory',
      optional: 'Optional',
    },
    phases: 'Phases',
    estimating: 'Estimate',
    resourcing: 'Resource',
    costing: 'Cost',
    review: 'Review',
    currentVersion: 'Version {versionName}',
  },
  estimationDetail: {
    approveButton: 'Approve',
    cancelReviewButton: 'Cancel Review',
    costEstimationItemsGrouped: {
      costUnit: 'Cost Unit',
    },
    costEstimationItemsTable: {
      referenceNumber: '#',
      subtotal: 'Subtotal',
      detailsButton: 'Details',
      assumptionsButton: 'Assumptions',
      commentsButton: 'Comments',
      addEstimationItemButton: 'Add Cost',
      emptyTableGroupName: 'Cost Name',
      emptyTableText: 'Currently there are no sub-costs for this cost.',
      included: 'Included',
    },
    estimationItemDeleteModal: {
      title: 'Deleting Phase',
      warningMessage:
        'This will delete the phase and all its sub-phases. The operation cannot be reverted.',
      confirmButton: 'Confirm Removing',
    },
    estimationItemDetailModal: {
      title: 'Edit Estimation Phase',
      assumptionsButton: 'Assumptions',
      commentsButton: 'Comments',
      removePhaseButton: 'Remove Phase',
      overrideOriginalEstimateButton: 'Update Original Estimate',
      subItemsTitle: 'Subitems in Phase',
      unsavedChangesTitle: 'Unsaved Changes',
      unsavedChangesWarning:
        'You have unsaved changes to this estimation phase. How would you like to proceed?',
      discardAndCloseButton: 'Discard and close',
    },
    estimationItemEditation: {
      descriptionPlaceholder: 'Meaningful short description for management (max 210 characters)',
      phaseName: 'Phase Name',
      phaseLead: 'Phase Lead',
      description: 'Description',
      methodology: 'Methodology',
      estimate: 'Estimate',
    },
    estimationItemsGrouped: {
      estimationUnit: 'Estimation Unit',
    },
    estimationItemsTable: {
      renameItem: 'Rename',
      saveItemName: 'Save name',
      cancelItemRename: 'Cancel rename',
      estimationUnit: 'Estimation Unit',
      referenceNumber: '#',
      phaseLead: 'Phase Lead',
      methodology: 'Methodology',
      estimate: 'Estimate',
      estimateCheck: 'Estimate Check',
      subtotal: 'Subtotal',
      included: 'Included',
      detailsButton: 'Details',
      assumptionsButton: 'Assumptions',
      commentsButton: 'Comments',
      addPhaseButton: 'Add Phase',
      suggestion: 'Suggestion',
      emptyTableGroupName: 'Phase Name',
      emptyTableText: 'Currently there are no subitems for this phase.',
    },
    estimationItemOmitModal: {
      title: 'Provide Reason',
      reason: {
        placeholder: 'Meaningful short description for management (max 210 characters)',
      },
      confirmButton: 'Confirm Removing',
    },
    startReviewButton: 'Start Review',
    tab: {
      project: {
        label: 'Project',
      },
      metadata: {
        label: 'Master Data',
        mandatoryTitle: 'Mandatory Master Data',
        optionalTitle: 'Optional Master Data',
        progressSentenceFormattedWithAmountPercentage:
          '{amountPercentage}% of mandatory information has been provided.',
        aiAdvisoryDocumentLinkLabel: 'AI Advisory Link',
        unsavedChangesTitle: 'Unsaved Changes',
        unsavedChangesWarning:
          'You have unsaved changes in the Master Data form. Do you want to save them before leaving?',
        unsavedChangesSave: 'Save and leave',
        unsavedChangesDiscard: 'Discard and leave',
        unsavedChangesCancel: 'Cancel',
        unsavedChangesValidationError:
          'Some mandatory fields are missing or invalid. Please fill them in before saving.',
      },
      estimation: {
        label: 'Estimation',
      },
      estimationAssumptions: {
        label: 'Assumptions',
      },
      resource: {
        label: 'Resource',
        resetNamesToDefaults: 'Reset all to defaults',
      },
      resourceCost: {
        label: 'Resource Cost',
      },
      directCost: {
        label: 'Direct Cost',
      },
      costSummary: {
        label: 'Summary of Costs',
      },
      review: {
        label: 'Review',
      },
      managementReport: {
        label: 'Management Report',
      },
    },
    deleteGroupModal: {
      title: 'Deleting Group',
      warningMessage:
        'This will delete the group and all its types will be moved to the ungrouped section.',
      confirmButton: 'Confirm Deleting',
    },
    reviewCommentsSummary: {
      title: 'Review comments summary',
      notAvailable:
        'No summary available yet. Complete a review cycle (Send Back or Approve) to see the summary here.',
    },
    sendBackButton: 'Send Back',
    currentVersion: 'version: {versionNumber}',
    activeVersion: 'Active version: {versionNumber}',
    workingVersion: 'Working version: {versionNumber}',
    createNewVersion: 'Create New Version',
    renameVersion: 'Rename Version',
    viewAllVersions: 'View all versions',
    versionName: 'Version Name',
    versionCreated: 'Version {versionName} has been successfully created.',
    versionRenamed: 'Estimation version has been successfully renamed.',
    methodologyChangeConfirmationModal: {
      title: 'Changing methodology',
      warningMessage:
        'This action will change methodology on all sub-phases. As a consequence, its parameters will be regenerated and the related entered estimates will get lost. The operation cannot be reverted.',
    },
    detail: {
      saved: 'Estimation item "{mainEstimationItemName}" has been saved.',
    },
    archivedModelBanner: {
      text: 'This estimation model has been archived. Existing values remain usable.',
      dismiss: 'Dismiss',
    },
    estimationItemsCostSummarySummaryTable: {
      estimate: 'Estimate',
      subtotal: 'Subtotal',
    },
    estimationItemsDirectCostSummaryTable: {
      estimate: 'Estimate',
      subtotal: 'Subtotal',
    },
    directCosts: {
      costBreakDownTitle: 'Direct Cost Breakdown',
      detail: {
        sum: 'Sum',
        estimationItemName: 'Cost Name',
        description: 'Description',
        title: 'Edit Cost',
        assumptionsButton: 'Assumptions',
        commentsButton: 'Comments',
        overrideOriginalEstimateButton: 'Update Original Estimate',
        subItemsTitle: 'Sub-costs',
        saved: 'Costs of sub-estimation items of "{mainEstimationItemName}" have been saved.',
      },
    },
    estimationItemsResourceCostSummaryTable: {
      estimate: 'Estimate',
      subtotal: 'Subtotal',
      totalEfforts: 'Total Efforts',
    },
    resourceCosts: {
      costPerUnitTitle: 'Cost per Resource Unit',
      currency: 'Currency',
      costBreakDownTitle: 'Resource Cost Breakdown',
      estimationUnit: 'Estimation Unit',
      costUnit: 'Cost Unit',
      subTotal: 'Subtotal',
      costsRecalculating: 'Recalculating',
    },
  },
  estimationVersions: {
    versionName: 'Version Name',
    status: 'Status',
    lastUpdated: 'Last Updated',
    lastUpdatedBy: 'Last Updated By',
    createdAt: 'Created At',
    createdBy: 'Created By',
    estimate: 'Estimate',
  },
  error: {
    code: {
      GENERAL: 'An error occurred.',
      VALIDATION: 'Validation error',
      DOMAIN_ERROR: 'An error occurred.',
      ENTITY_NOT_FOUND: 'Entity not found.',
      ESTIMATION_ITEM_ASSUMPTION_NOT_FOUND: 'Assumption not found.',
      ESTIMATION_ITEM_NOT_FOUND: 'Phase not found.',
      ESTIMATION_NOT_FOUND: 'Estimation not found.',
      PROJECT_TYPE_CURRENCY_NOT_FOUND: 'Unsupported currency.',
      PROJECT_TYPE_NOT_FOUND: 'Project type not found.',
      PROJECT_NOT_FOUND: 'Project not found.',
      ESTIMATION_COST_NOT_FOUND: 'Estimation cost not found.',
      RESOURCE_TYPE_COST_NOT_FOUND: 'Resource type not found.',
      USER_NOT_FOUND: 'User not found.',
      LOCALIZATION_NOT_FOUND: 'Localization not found.',
      ESTIMATION_USER_ASSIGNMENT_NOT_FOUND: 'Estimation - User assingment not found.',
      ESTIMATION_MODEL_ITEM_NOT_FOUND: 'Estimation model item not found.',
      ESTIMATION_ITEM_COMMENT_NOT_FOUND: 'Phase comment not found.',
      ESTIMATION_COMMENT_NOT_FOUND: 'Estimation comment not found.',
      COMMENT_BASE_NOT_FOUND: 'Comment not found.',
      TENANT_NOT_FOUND: 'Tenant not found.',
      ROLE_NOT_FOUND: 'Role not found.',
      PROJECT_NOTIFICATION_NOT_FOUND: 'Project notification not found.',
      NOTIFICATION_BASE_NOT_FOUND: 'Notification not found.',
      ESTIMATION_NOTIFICATION_NOT_FOUND: 'Estimation notification not found.',
      ESTIMATION_ITEM_NOTIFICATION_NOT_FOUND: 'Phase notification not found.',
      COMMENT_NOTIFICATION_NOT_FOUND: 'Comment notification not found.',
      ESTIMATION_MODEL_UNIT_NOT_FOUND: 'Estimation model unit not found.',
      ESTIMATION_MODEL_ITEM_PARAMETER_NOT_FOUND: 'Estimation model item parameter not found.',
      ESTIMATION_MODEL_ASSUMPTION_CATEGORY_NOT_FOUND: 'Assumption type not found.',
      ESTIMATION_MODEL_ADDITIONAL_FIELD_DEFINITION_NOT_FOUND: 'Metadata field not found.',
      ESTIMATION_MODEL_NOT_FOUND: 'Estimation model not found.',
      METHODOLOGY_PARAMETER_NOT_FOUND: 'Methodology parameter not found.',
      METHODOLOGY_NOT_FOUND: 'Methodology not found.',
      ESTIMATION_ITEM_RESOURCE_NOT_FOUND: 'Phase resource not found.',
      ESTIMATION_ITEM_COST_NOT_FOUND: 'Phase cost not found.',
      ESTIMATION_ITEM_PARAMETER_NOT_FOUND: 'Phase parameter not found.',
      ESTIMATION_ASSUMPTION_NOT_FOUND: 'Estimation assumption not found.',
      ESTIMATION_ADDITIONAL_FIELD_NOT_FOUND: 'Estimation metadata field not found.',
      CURRENCY_CONVERSION_RATE_NOT_FOUND: 'Currency conversion rate not found.',
      ESTIMATION_MODEL_METHODOLOGY_NOT_FOUND: 'Estimation model methodology not found.',
      RESOURCE_TYPE_NOT_FOUND: 'Resource type not found.',
      COST_TYPE_NOT_FOUND: 'Cost type not found.',
      CURRENCY_NOT_FOUND: 'Currency not found.',
      CALCULATION_FORMULA_EVALUATION_ERROR: 'Calculation formula evaluation error.',
      SUM_ITEMS_EXCEPT_ARGUMENTS_MUST_BE_QUOTED:
        "sumItemsExcept function arguments must be quoted. Use sumItemsExcept('item1', 'item2') instead of sumItemsExcept(item1, item2).",
      CALCULATION_MISSING_INPUT_DATA: 'Calculation is missing input data.',
      CALCULATION_MISSING_PARAMETER_VALUE: 'Calculation is missing parameter value.',
      WRONG_ASSUMPTION_CATEGORY: 'Wrong assumption type.',
      WRONG_UNIT: 'Wrong estimation unit.',
      WRONG_ESTIMATION_UNIT_PARENT: 'Wrong estimation unit',
      CALCULATED_ESTIMATION_ITEM_CANNOT_HAVE_PARAMS: 'Calculated phase cannot have parameters.',
      CALCULATED_ESTIMATION_ITEM_CANNOT_HAVE_METHODOLOGY:
        'Calculated phase cannot have methodology.',
      UNCALCULATED_ESTIMATION_ITEM_MUST_HAVE_PARAMS: 'Modifiable phase must have parameters.',
      METHODOLOGY_CANNOT_BE_SET_WHEN_MODEL_ITEM_HAS_PARAMS:
        'Methodology cannot be set when the underlying model phase has parameters.',
      METHODOLOGY_UNSUPPORTED_BY_MODEL: 'The methodology is unsupported by the model.',
      OMIT_REASON_CAN_ONLY_BE_SET_FOR_OMITTED_ESTIMATION_ITEM:
        'Omit reason can only be set for omitted phase.',
      OMIT_REASON_MUST_BE_SET_FOR_OMITTED_ESTIMATION_ITEM:
        'Omit reason must be set for required omitted phase.',
      ESTIMATION_ADDITIONAL_FIELD_WRONG_DEFINITION:
        'Wrong definition of the estimation metadata field.',
      CANNOT_PARSE_ADDITIONAL_FIELD_OF_TYPE_DURATION: 'Cannot parse duration metadata field.',
      ESTIMATION_ADDITIONAL_FIELD_OF_TYPE_SELECTABLE_CONTAINS_WRONG_VALUE:
        'Estimation selectable metadata field contains wrong value.',
      USER_NOT_REVIEWER: 'The user is not reviewer.',
      CALCULATION_FORMULA_MUST_BE_SET_FOR_METHODOLOGY:
        'Calculation formula must be set for methodology.',
      METHODOLOGY_MUST_HAVE_AT_LEAST_ONE_PARAMETER: 'Methodology must have at least one parameter.',
      CANNOT_DELETE_METHODOLOGY_IN_USE:
        'Cannot delete methodology because it is used in estimation model(s).',
      ESTIMATION_ITEM_BASED_ON_MODEL_CANNOT_HAVE_THIS_PARENT:
        'Phase based on model cannot have this parent phase.',
      WRONG_ESTIMATION_ITEMS_PARAMETERS_COUNT: 'Wrong number of parameters for the phase.',
      WRONG_CALCULATION_METHODOLOGY_FORMULA: 'Methodology has wrong calculation formula.',
      AI_INTEGRATION_UNEXPECTED_FINISH_REASON: 'Unexpected finish reason in AI services.',
      AI_INTEGRATION_NO_CONTENT: 'Missing content in AI integration.',
      WRONG_RESOURCES_FOR_ESTIMATION_ITEM: 'Wrong resource(s) for phase.',
      WRONG_RESOURCE_TYPE_COSTS_FOR_ESTIMATION: 'Wrong resource type costs for estimation.',
      CANNOT_DEFINE_COSTS_WHEN_THERE_ARE_NO_RESOURCE_TYPES_DEFINED:
        'Costs cannot be defined when there are no resource types defined.',
      ESTIMATION_ITEM_FOR_ESTIMATION_MODEL_ITEM_MUST_HAVE_SAME_GROUP_AS_THE_MODEL_ITEM:
        'Model based phase must have same group as the model phase definition.',
      WRONG_ESTIMATION_MODEL_GROUP_ID: 'Wrong estimation model group.',
      UNAUTHORIZED_USER_ACCESS: 'Unauthorized user access.',
      USER_PROVISIONING_MISSING_EMAIL_CLAIM:
        "We're sorry, but your login doesn't contain an email address. Please contact your administrator.",
      ESTIMATION_MODEL_ITEMS_CANNOT_HAVE_SAME_COMBINATION_OF_PARENT_ID_ORDER_AND_PURPOSE:
        'Estimation model phase definitions having same parent cannot have same order and purpose defined.',
      ESTIMATION_MODEL_ITEMS_CANNOT_HAVE_SAME_REF_KEY:
        'Estimation model phase definitions cannot have same reference key.',
      ESTIMATION_MODEL_ITEMS_CANNOT_HAVE_SAME_CALCULATION_REF:
        'Estimation model phase definitions cannot have same reference name.',
      ESTIMATION_MODEL_WITH_SAME_PROJECT_TYPE_BUT_DIFFERENT_REF_KEY_EXISTS:
        'Estimation model with same project type but different ref_key exists.',
      ESTIMATION_MUST_BE_APPROVED_WHEN_CREATING_NEW_VERSION:
        'Estimation must be completed when creating a new version.',
      CALCULATION_FORMULA_MUST_BE_PROVIDED_WHEN_HAVING_MULTIPLE_PARAMETERS:
        'Calculation formula must be provided when there are multiple parameters defined.',
      TENANT_NOT_AVAILABLE: 'The tenant is unavailable.',
      GRAPH_API_EMPTY_USER_RESULT: 'Graph API returned empty user.',
      CANNOT_PARSE_GRAPH_API_USER_ID: 'Cannot parse Graph API user ID.',
      CANNOT_CHANGE_ESTIMATION_ITEM_METHODOLOGY_ON_CHILD_ITEM:
        'Cannot change estimation item methodology on sub-item.',
      CANNOT_CREATE_INITIAL_ESTIMATION_ITEMS_WHEN_ANY_ITEMS_ALREADY_EXIST:
        'Cannot create initial estimation items when any items already exist',
      ESTIMATION_ITEM_PARAMETER_VALUES_COUNT_MISMATCH:
        'Estimation item parameter values count does not match',
      ESTIMATION_ITEM_PARAMETER_CREATION_FAILED: 'Estimation item parameter creation failed.',
      PUBLISHED_ESTIMATION_MODEL_CANNOT_BE_UNPUBLISHED:
        'Cannot unpublish the estimation model that has already been published.',
      DUPLICATE_METHODOLOGY_ID_IN_ESTIMATION_MODEL: 'Duplicate methodology id in estimation model.',
      ESTIMATION_MODEL_UNIT_MISMATCH: 'Units must be related to estimation model.',
      DUPLICATE_ESTIMATION_UNIT_IN_ESTIMATION_MODEL:
        'Duplicate estimation unit in estimation model.',
      ESTIMATION_MODEL_ADDITIONAL_FIELD_TYPE_MUST_BE_SELECTABLE:
        'Estimation model metadata type must be selectable.',
      ESTIMATION_MODEL_ADDITIONAL_FIELD_OPTIONS_MUST_NOT_BE_EMPTY:
        'Estimation model metadata options must not be empty.',
      ESTIMATION_MODEL_ADDITIONAL_FIELD_OPTION_MUST_NOT_CONTAIN_PIPE:
        'Selectable option cannot contain character "|"',
      SELF_REFERENCING_PARENT_ERROR: 'Self referencing parent.',
      CALCULATION_FORMULA_MISSING: 'Calculation formula is missing.',
      INVALID_CALCULATION_REFERENCE: 'Invalid calculation reference',
      FILTER_COMBINATION_IS_NOT_SUPPORTED: 'The filter combination is unsupported.',
      PARENT_AND_CHILD_COMMENT_DISCRIMINATOR_TYPE_MISMATCH: 'Mismatch in comment type.',
      WRONG_COSTS_FOR_ESTIMATION_ITEM: 'Wrong costs for estimation item.',
      ESTIMATION_MODEL_ITEM_HAS_RECURSIVE_FORMULA_REFERENCE:
        'Estimation model item has recursive formula reference.',
      ESTIMATION_MODEL_ITEM_HAS_WRONG_FORMULA_REFERENCE:
        'Estimation model item has wrong formula reference.',
      ESTIMATION_MODEL_ITEM_HAS_MISSING_FORMULA_PARAMETERS:
        'Estimation model item has missing formula parameters.',
      ESTIMATION_MODEL_ITEM_REFERENCES_NON_EXISTING_ITEM:
        'Estimation model item references non-existing item in its formula. Please check formulas and reference names.',
      DECIMAL_VALUE_TOO_MANY_DIGITS: 'Decimal value has too many digits.',
      DECIMAL_VALUE_TOO_MANY_DECIMAL_DIGITS: 'Decimal value has too many decimal digits.',
      CANNOT_SET_PARAMETERS_ON_ITEM_WITH_COST_PURPOSE:
        'Cannot set parameters on item with cost purpose.',
      ESTIMATION_ITEM_PURPOSE_MUST_MATCH_ITS_ESTIMATION_MODEL_ITEM_PURPOSE:
        'Estimation item purpose must match its estimation model item purpose.',
      CANNOT_SET_METHODOLOGY_ON_ITEM_WITH_COST_PURPOSE:
        'Cannot set methodology on estimation item with cost purpose.',
      ESTIMATION_ITEM_OF_COST_PURPOSE_CANNOT_HAVE_CALCULATED_RESULT:
        'Estimation item of cost purpose cannot have calculated result.',
      PARENT_ESTIMATION_ITEM_PURPOSE_MUST_MATCH: 'Parent estimation item purpose must match.',
      ESTIMATION_MODEL_ITEM_PARENT_PURPOSE_MUST_MATCH:
        'Estimation model item parent purpose must match.',
      CANNOT_SET_METHODOLOGY_ON_ESTIMATION_MODEL_ITEM_WITH_COST_PURPOSE:
        'Cannot set methodology on estimation model item with cost purpose.',
      ESTIMATION_MODEL_ITEM_WITH_COST_PURPOSE_CANNOT_HAVE_CALCULATION_FORMULA:
        'Estimation model item with cost purpose cannot have calculation formula.',
      ESTIMATION_MODEL_ITEM_WITH_COST_PURPOSE_CANNOT_HAVE_PARAMETERS:
        'Estimation model item with cost purpose cannot have parameters.',
      CANNOT_ASSIGN_RESOURCE_TO_ESTIMATION_ITEM_WITH_COST_PURPOSE:
        'Cannot assign resource to estimation item with cost purpose.',
      CALCULATED_ESTIMATION_MODEL_ITEM_CANNOT_HAVE_PARAMETER_WITH_NULL_VALUE:
        'Calculated estimation model item cannot have parameter with unset value',
      ESTIMATION_STAGE_MUST_BE_IN_REVIEW_WHEN_CHANGING_TO_APPROVED_STAGE:
        'Estimation stage must be in review when changing to approved stage.',
      ESTIMATION_STAGE_MUST_BE_IN_PROCESS_WHEN_CHANGING_TO_IN_REVIEW_STAGE:
        'Estimation stage must be in process when changing to in review stage.',
      ESTIMATION_STAGE_MUST_BE_IN_CREATION_OR_IN_REVIEW_WHEN_CHANGING_TO_IN_PROCESS_STAGE:
        'Estimation stage must be in creation or in review when changing to in process stage.',
      CANNOT_UPDATE_ESTIMATION_WHEN_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation when stage is in review or approved.',
      CANNOT_UPDATE_ESTIMATION_ADDITIONAL_FIELDS_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation metadata fields when estimation stage is in review or approved.',
      CANNOT_UPSERT_ESTIMATION_COSTS_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation costs when estimation stage is in review or approved.',
      CANNOT_CREATE_ESTIMATION_ITEM_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot create estimation item when estimation stage is in review or approved.',
      CANNOT_UPSERT_ESTIMATION_ITEMS_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation items when estimation stage is in review or approved.',
      CANNOT_UPDATE_ESTIMATION_ITEM_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation item when estimation stage is in review or approved.',
      CANNOT_DELETE_ESTIMATION_ITEM_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot delete estimation item when estimation stage is in review or approved.',
      CANNOT_CREATE_ESTIMATION_ITEM_ASSUMPTION_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot create estimation item assumption when estimation stage is in review or approved.',
      CANNOT_UPDATE_ESTIMATION_ITEM_ASSUMPTION_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation item assumption when estimation stage is in review or approved.',
      CANNOT_DELETE_ESTIMATION_ITEM_ASSUMPTION_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot delete estimation item assumption when estimation stage is in review or approved.',
      CANNOT_UPSERT_ESTIMATION_ITEM_COSTS_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation item costs when estimation stage is in review or approved.',
      CANNOT_UPSERT_ESTIMATION_ITEM_RESOURCES_WHEN_ESTIMATION_STAGE_IS_IN_REVIEW_OR_APPROVED:
        'Cannot update estimation item resources when estimation stage is in review or approved.',
      CANNOT_DELETE_ESTIMATION_ITEM_WITH_ESTIMATION_MODEL_ITEM:
        'Cannot delete estimation item with estimation model item.',
      CANNOT_PARSE_ADDITIONAL_FIELD_OF_TYPE_DATE: 'Date master data are in wrong format.',
      CANNOT_PARSE_ADDITIONAL_FIELD_OF_TYPE_DOCUMENT_LINK:
        'Document link metadata must be a valid URL.',
      CANNOT_COMMENT_WHEN_ESTIMATION_IS_NOT_IN_REVIEW:
        'Cannot comment when estimation is not in review.',
      THERE_MUST_BE_EXACTLY_ONE_PLANNING_RESPONSIBLE_FOR_ESTIMATION:
        'There must be exactly one planning responsible per estimation.',
      CANNOT_DELETE_USER_WHEN_IS_PLANNING_RESPONSIBLE:
        'User is assigned as planning responsible on estimation(s) therefore cannot be deleted.',
      EVERY_PHASE_LEAD_MUST_BE_ASSIGNED_AS_CONTRIBUTOR_OR_PLANNING_RESPONSIBLE_TO_ESTIMATION:
        'Every phase lead must be assigned as contributor or a planning responsible to the estimation.',
    },
  },
  global: {
    appName: 'Estimator',
    initializatingSystem: 'Initializing system, please wait',
    signInFailed: 'Sign-in failed. Please try again, or contact support if this keeps happening.',
    signInRetry: 'Try again',
    provisioningBlocked:
      'Your account could not be set up because your sign-in is missing required information. Please contact your administrator or support with the reference below.',
    resetPasswordOrResendInvite: 'Reset your password / ask your admin to resend the invite',
    signOut: 'Sign out',
    continueEstimationOf: 'Continue estimation of',
    continueEstimation: 'Continue estimation',
    saveAllChanges: 'Save all changes',
    saveAndClose: 'Save and close',
    save: 'Save',
    create: 'Create',
    close: 'Close',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    copy: 'Copy',
    publish: 'Publish',
    paginationText: '`Showing  {from} to {to} of {totalCount} entries`',
    loading: 'Loading...',
    saving: 'Saving...',
    NoDataFound: 'No data found',
    NoTenantsFound: 'No tenants found',
    search: 'Search Term...',
    status: 'Status',
    noOfUsers: 'Number of Users',
    createdAt: 'Created At',
    yes: 'Yes',
    no: 'No',
    prevStep: 'Previous step',
    nextStep: 'Next step',
    export: 'Export',
    notAvailable: 'N/A',
    areYouSureYouWantToProceed: 'Are you sure you want to proceed?',
    columns: 'Columns',
    selectLanguage: 'Select language',
    unexpectedError: 'Unexpected error occurs. Please contact support.',
    chooseDate: 'Choose date',
    savedSuccessfully: 'Changes have been successfully saved.',
    emptyContext: 'No data found, redirecting to the first step of wizard.',
    tenantStylesNotLoaded: 'Tenant styles failed to load.',
    inactive: 'INACTIVE',
    next: 'Next',
    finish: 'Finish',
    back: 'Back',
    filter: 'Filter',
    applyFilters: 'Apply filters',
    select: 'Select...',
    estimation: 'Estimation',
  },
  table: {
    pagination: {
      message: 'Showing {from} to {to} of {totalRecords} entries',
    },
  },
  estimationTemplate: {
    duplicateReferenceName: 'Reference name must be unique',
  },
  estimation: {
    detail: {
      managementReport: {
        basicProjectData: 'Basic Project data',
        fields: 'Fields',
        resourceCostBreakdownSummary: 'Resource Cost Breakdown Summary',
        resourceCostBreakdownDetail: 'Resource Cost Breakdown Detail',
        resourceCostBreakdownSummaryTable: {
          emptyTableText: 'No resource cost items to display',
          projectPhase: 'Project Phase',
        },
        summaryTable: {
          subtotal: 'Subtotal',
          emptyTableText: 'No data available',
          projectPhase: 'Project Phase',
          reference: 'Reference',
          estimate: 'Estimate',
          assumptions: 'Assumptions',
          totalEfforts: 'Total Efforts',
          directCostItem: 'Direct Cost Item',
          total: 'Total',
          noAssumptions: 'No assumptions',
          noDirectCostItems: 'No direct cost items to display',
          comments: 'Comments',
        },
      },
    },
  },
  notificationSettings: {
    header: 'Notification Settings',
    description:
      'Configure how users receive notifications for workflow events. Settings apply tenant-wide per role.',
    loading: 'Loading notification settings...',
    saveSuccess: 'Notification settings saved successfully.',
    saveError: 'Failed to save notification settings.',
    workflowEvent: 'Workflow Event',
    role: 'Role',
    deliveryMode: 'Delivery Mode',
    contributor: 'Contributor',
    planningResponsible: 'Planning Responsible',
    reviewer: 'Reviewer',
    none: 'None',
    inApp: 'In-App',
    email: 'Email',
    both: 'Both',
    userAssignedAsContributor: 'User Assigned as Contributor',
    userAssignedAsPlanningResponsible: 'User Assigned as Planning Responsible',
    userAssignedAsReviewer: 'User Assigned as Reviewer',
    estimationReviewRequested: 'Estimation Submitted for Review',
    estimationReviewCancelled: 'Review Cancelled',
    estimationApproved: 'Estimation Approved',
    estimationNewVersionCreated: 'New Version Created',
    estimationAiReviewCreated: 'AI Review Created',
    userAssignedAsPhaseLead: 'User Assigned as Phase Lead',
    commentAddedToEstimationItem: 'Comment Added to Estimation Item',
    reviewCommentsSummaryAvailable: 'Review Comments Summary Available',
    estimationReviewOutcome: 'Estimation Review: Outcome',
    phaseStatusChanged: 'Phase Status Changed',
    phaseBlocked: 'Phase Blocked',
    phaseUnblocked: 'Phase Unblocked',
    itemAssignedToPhase: 'Item Assigned to Phase',
    phaseDeadlineApproaching: 'Phase Deadline Approaching',
    conversationShared: 'AI Advisory Conversation Shared',
  },
};
